import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { HistoryOutlined } from '@ant-design/icons'
import '../../styles/aurora-page.css'
import * as timersApi from '@/api/timers'
import type { TimerRun, TimerSchedule } from '@/types'
import { useProjectStore } from '@/stores/project'

const { Title, Text } = Typography

const TYPE_META: Record<string, { label: string; color: string }> = {
  scenario: { label: '接口场景', color: 'blue' },
  ui_script: { label: 'UI 用例', color: 'purple' },
}

const STATUS_META: Record<number, { label: string; color: string }> = {
  1: { label: '执行中', color: 'processing' },
  2: { label: '成功', color: 'success' },
  3: { label: '失败', color: 'error' },
  4: { label: '错误', color: 'volcano' },
}

function formatDuration(ms: number): string {
  if (!ms) return '-'
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

/**
 * 定时器执行记录页（测试计划 → 定时器执行记录）
 * - 记录每次 cron 触发的执行结果（接口场景由后端执行器直接回写，UI 用例由 worker 回调回写）
 * - 可跳转测试报告（?id= 深链）与定时器计划页
 */
export default function TimerRuns() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  const initialScheduleId = Number(searchParams.get('schedule_id')) || undefined

  const [runs, setRuns] = useState<TimerRun[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [scheduleFilter, setScheduleFilter] = useState<number | undefined>(initialScheduleId)
  const [statusFilter, setStatusFilter] = useState<number | undefined>()
  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [scheduleOptions, setScheduleOptions] = useState<TimerSchedule[]>([])

  const loadRuns = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await timersApi.listRuns({
        project_id: projectId,
        schedule_id: scheduleFilter,
        status: statusFilter,
        target_type: typeFilter,
        page,
        page_size: pageSize,
      })
      setRuns(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, scheduleFilter, statusFilter, typeFilter, page, pageSize])

  useEffect(() => {
    loadRuns()
  }, [loadRuns])

  // 计划筛选下拉（分页上限内）
  useEffect(() => {
    if (!projectId) return
    timersApi
      .listSchedules({ project_id: projectId, page: 1, page_size: 100 })
      .then((res) => setScheduleOptions(res.data))
      .catch(() => setScheduleOptions([]))
  }, [projectId])

  const columns: ColumnsType<TimerRun> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '计划名称', dataIndex: 'schedule_name', width: 180, ellipsis: true },
    {
      title: '类型',
      dataIndex: 'target_type',
      width: 100,
      render: (v: string) => <Tag color={TYPE_META[v]?.color || 'default'}>{TYPE_META[v]?.label || v}</Tag>,
    },
    { title: '执行目标', dataIndex: 'target_name', width: 170, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: number, r) => {
        const meta = STATUS_META[v] || { label: `未知(${v})`, color: 'default' }
        const tag = <Tag color={meta.color}>{meta.label}</Tag>
        // 失败/错误时悬浮展示原因
        return (v === 3 || v === 4) && r.error ? (
          <Tooltip title={<div style={{ maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{r.error}</div>}>
            <span style={{ cursor: 'help' }}>{tag}</span>
          </Tooltip>
        ) : (
          tag
        )
      },
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      width: 165,
      render: (v: string | null) =>
        v ? <span style={{ fontSize: 13 }}>{new Date(v).toLocaleString('zh-CN', { hour12: false })}</span> : '-',
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      width: 90,
      render: (v: number) => formatDuration(v),
    },
    {
      title: '结果关联',
      key: 'refs',
      width: 150,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.report_id ? (
            <Button
              type="link"
              size="small"
              style={{ padding: '0 4px' }}
              onClick={() => navigate(`/reports?id=${r.report_id}`)}
            >
              查看报告
            </Button>
          ) : null}
          {r.execution_id ? (
            <Tooltip title="UI 用例执行记录 ID（执行中心可查）">
              <Text type="secondary" style={{ fontSize: 12 }}>执行 #{r.execution_id}</Text>
            </Tooltip>
          ) : null}
          {!r.report_id && !r.execution_id ? <Text type="secondary" style={{ fontSize: 12 }}>-</Text> : null}
        </Space>
      ),
    },
    {
      title: '失败原因',
      dataIndex: 'error',
      ellipsis: true,
      render: (v: string) => (v ? <Text style={{ fontSize: 13 }}>{v}</Text> : <Text type="secondary">-</Text>),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>定时器执行记录</Title>
          <Tag color="geekblue">定时触发结果</Tag>
        </div>
        <Button onClick={loadRuns}>刷新</Button>
      </div>

      <div className="content-card">
        <div className="content-card-header">
          <div className="content-card-title">
            <HistoryOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
            执行记录
          </div>
          <Space size={8} wrap>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="按定时计划筛选"
              style={{ width: 200 }}
              value={scheduleFilter}
              onChange={(v) => {
                setScheduleFilter(v)
                setPage(1)
              }}
              options={scheduleOptions.map((s) => ({ value: s.id, label: s.name }))}
            />
            <Select
              allowClear
              placeholder="按状态筛选"
              style={{ width: 130 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v)
                setPage(1)
              }}
              options={Object.entries(STATUS_META).map(([k, m]) => ({ value: Number(k), label: m.label }))}
            />
            <Select
              allowClear
              placeholder="按类型筛选"
              style={{ width: 130 }}
              value={typeFilter}
              onChange={(v) => {
                setTypeFilter(v)
                setPage(1)
              }}
              options={Object.entries(TYPE_META).map(([k, m]) => ({ value: k, label: m.label }))}
            />
          </Space>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={runs}
            size="middle"
            scroll={{ y: '100%' }}
            locale={{ emptyText: '暂无执行记录，到「定时器」页新建计划后到点自动触发' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              },
            }}
          />
        </div>
      </div>
    </div>
  )
}
